import { useState, useCallback, useEffect } from 'react';
import { loadUserFtpWatts, saveUserFtpWatts } from '@/features/user-zones';

const STORAGE_KEY = 'swb_cycling_ftp_watts';

function readStoredFtp(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null || raw === '') return null;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 30 || n > 750) return null;
    return n;
  } catch {
    return null;
  }
}

/**
 * Cycling FTP (watts) for FIT exports: guests use localStorage; signed-in users load/save `profiles.cycling_ftp` / `profiles.ftp` (legacy) mirrored in `user_settings.ftp`.
 */
export function useStoredFtp(userId?: string | null) {
  const [ftp, setFtpState] = useState<number | null>(() => (userId ? null : readStoredFtp()));
  const [ftpSaving, setFtpSaving] = useState(false);

  useEffect(() => {
    if (!userId) {
      setFtpState(readStoredFtp());
      return;
    }

    let cancelled = false;
    void (async () => {
      const fromDb = await loadUserFtpWatts(userId);
      if (cancelled) return;
      setFtpState(fromDb ?? readStoredFtp() ?? null);
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setFtp = useCallback(
    async (n: number): Promise<{ ok: boolean; error?: string }> => {
      const rounded = Math.round(Math.max(30, Math.min(750, n)));
      try {
        localStorage.setItem(STORAGE_KEY, String(rounded));
      } catch {
        /* ignore */
      }
      setFtpState(rounded);

      if (!userId) {
        return { ok: true };
      }

      setFtpSaving(true);
      try {
        return await saveUserFtpWatts(userId, rounded);
      } finally {
        setFtpSaving(false);
      }
    },
    [userId],
  );

  return { ftp, setFtp, ftpSaving };
}
